/* ==========================================================================
   MYCUP theme — mycup.js
   Small, dependency-free progressive enhancement. The store works without it.
   - Scroll reveal, header state, hero parallax, hero video
   - Heat / cool demo, hotspots
   - Buy box: variant switching, gallery, zoom, quantity
   - AJAX cart drawer (Section Rendering API), cart page quantity
   - Mobile sticky purchase bar
   - Custom analytics events via Shopify.analytics.publish (no trackers here;
     GA4 / Google Ads / Meta run through Shopify apps or Customer Events)
   ========================================================================== */
(() => {
  'use strict';

  const cfg = window.MYCUP || { routes: {}, strings: {} };
  const routes = cfg.routes || {};
  const strings = cfg.strings || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches || document.body.classList.contains('no-motion');
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------- Analytics: custom events only (no double-firing) ---------- */
  const publish = (name, data = {}) => {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(name, data);
      }
    } catch (e) { /* never block the UI for analytics */ }
  };
  document.addEventListener('click', (e) => {
    const el = e.target.closest('[data-track]');
    if (el) publish('mycup_cta_click', { location: el.dataset.track });
  });

  /* ---------- Header scrolled state ---------- */
  const header = $('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Mobile nav: close on link / Escape ---------- */
  const mobileNav = $('[data-mobile-nav]');
  if (mobileNav) {
    mobileNav.addEventListener('click', (e) => { if (e.target.closest('[data-close-nav]')) mobileNav.removeAttribute('open'); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && mobileNav.open) { mobileNav.removeAttribute('open'); $('summary', mobileNav).focus(); } });
  }

  /* ---------- Scroll reveal ---------- */
  const revealEls = $$('[data-reveal]');
  if (!reduceMotion && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -5% 0px', threshold: 0 });
    revealEls.forEach((el) => io.observe(el));
  } else {
    revealEls.forEach((el) => el.classList.add('is-visible'));
  }

  /* ---------- Hero parallax (transform only, rAF-throttled) ---------- */
  const parallax = $('[data-parallax]');
  if (parallax && !reduceMotion) {
    const media = $$('.hero__img, .hero__video', parallax);
    let ticking = false;
    const update = () => {
      const rect = parallax.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < window.innerHeight) {
        const shift = Math.max(-28, Math.min(28, rect.top * -0.08));
        media.forEach((m) => m.style.setProperty('--py', `${shift.toFixed(1)}px`));
      }
      ticking = false;
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
  }

  /* ---------- Hero video: only when motion + data allow ---------- */
  $$('[data-autoplay-video]').forEach((video) => {
    const saveData = navigator.connection && navigator.connection.saveData;
    if (reduceMotion || saveData) return;
    video.preload = 'auto';
    const p = video.play();
    if (p && p.catch) p.catch(() => {});
  });

  /* ---------- Heat / cool demo ---------- */
  $$('[data-modes]').forEach((section) => {
    const buttons = $$('[data-mode-btn]', section);
    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const mode = btn.dataset.modeBtn;
        section.dataset.mode = mode;
        buttons.forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
        publish('mycup_mode_select', { mode });
      });
    });
  });

  /* ---------- Hotspots: one open at a time, close outside / Escape ---------- */
  const hotspots = $$('[data-hotspot]');
  if (hotspots.length) {
    hotspots.forEach((h) => h.addEventListener('toggle', () => {
      if (h.open) hotspots.forEach((o) => { if (o !== h) o.removeAttribute('open'); });
    }));
    document.addEventListener('click', (e) => { if (!e.target.closest('[data-hotspot]')) hotspots.forEach((h) => h.removeAttribute('open')); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') hotspots.forEach((h) => h.removeAttribute('open')); });
  }

  /* ---------- Cart drawer ---------- */
  const drawer = {
    el: $('[data-cart-drawer]'),
    lastFocus: null,
    open(trigger) {
      if (!this.el) return;
      this.lastFocus = trigger || document.activeElement;
      this.el.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => this.el.classList.add('is-open')));
      document.body.classList.add('drawer-open');
      const panel = $('.drawer__panel', this.el);
      setTimeout(() => panel && panel.focus(), 60);
      sticky.hide(true);
    },
    close() {
      if (!this.el || this.el.hidden) return;
      this.el.classList.remove('is-open');
      document.body.classList.remove('drawer-open');
      setTimeout(() => { this.el.hidden = true; }, 380);
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
      sticky.hide(false);
    },
    render(html) {
      if (!this.el || !html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-cart-drawer]');
      if (!fresh) return;
      $('.drawer__panel', this.el).innerHTML = $('.drawer__panel', fresh).innerHTML;
      const count = parseInt(fresh.dataset.itemCount || '0', 10);
      $$('[data-cart-count]').forEach((c) => {
        c.textContent = count;
        c.hidden = count === 0;
        c.classList.remove('is-bump'); void c.offsetWidth; c.classList.add('is-bump');
      });
      const status = $('[data-cart-status]', this.el);
      if (status) status.textContent = strings.added || '';
    },
    async change(key, quantity) {
      this.el.classList.add('is-busy');
      try {
        const res = await fetch(`${routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: 'cart-drawer', sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || strings.cartError);
        this.render(data.sections && data.sections['cart-drawer']);
      } catch (err) {
        window.location.href = routes.cart;
      } finally {
        this.el.classList.remove('is-busy');
      }
    }
  };

  /* ---------- Sticky purchase bar ---------- */
  const sticky = {
    el: $('[data-sticky-bar]'),
    dismissed: false,
    heroOut: false,
    buyIn: false,
    forceHidden: false,
    init() {
      if (!this.el || !('IntersectionObserver' in window)) return;
      try { this.dismissed = sessionStorage.getItem('mycup-sticky-dismissed') === '1'; } catch (e) { this.dismissed = false; }
      if (this.dismissed) return;
      this.el.hidden = false;
      const hero = $('.hero') || $('[data-buy-section]');
      const buy = $('[data-buy-section]');
      const footer = $('.section-footer');
      if (hero) new IntersectionObserver(([en]) => { this.heroOut = !en.isIntersecting && en.boundingClientRect.top < 0; this.sync(); }).observe(hero);
      if (buy) new IntersectionObserver((ens) => { this.buyIn = ens.some((en) => en.isIntersecting); this.sync(); }, { threshold: 0 }).observe(buy);
      if (footer) new IntersectionObserver(([en]) => { this.footerIn = en.isIntersecting; this.sync(); }).observe(footer);
      $('[data-sticky-dismiss]', this.el).addEventListener('click', () => {
        this.dismissed = true;
        try { sessionStorage.setItem('mycup-sticky-dismissed', '1'); } catch (e) {}
        this.sync();
      });
    },
    hide(state) { this.forceHidden = state; this.sync(); },
    sync() {
      if (!this.el) return;
      const show = this.heroOut && !this.buyIn && !this.footerIn && !this.dismissed && !this.forceHidden;
      this.el.classList.toggle('is-visible', show);
      this.el.setAttribute('aria-hidden', String(!show));
      $$('a, button', this.el).forEach((n) => { n.tabIndex = show ? 0 : -1; });
      document.body.classList.toggle('has-sticky', show);
    }
  };
  /* ---------- Buy box ---------- */
  $$('[data-buy-box]').forEach((box) => {
    let variants = [];
    try { variants = JSON.parse($('[data-variant-json]', box).textContent); } catch (e) { variants = []; }
    const form = $('[data-product-form]', box);
    const priceEl = $('[data-price-current]', box);
    const addBtn = $('[data-add-button]', box);
    const addLabel = $('[data-add-label]', box);
    const selectedLabel = $('[data-selected-label]', box);
    const slides = $$('[data-slide]', box);
    const thumbsWrap = $('[data-thumbs]', box);
    const stage = $('[data-zoom]', box);
    const isProductPage = /\/products\//.test(window.location.pathname);

    // Build thumbnails from slides (keeps markup single-sourced)
    const thumbs = [];
    if (thumbsWrap && slides.length > 1) {
      slides.forEach((slide, i) => {
        const img = $('img', slide);
        if (!img) return;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'buy__thumb' + (slide.classList.contains('buy__slide--photo') ? ' buy__thumb--photo' : '');
        btn.setAttribute('aria-label', `${img.alt || ''} (${i + 1}/${slides.length})`);
        const t = document.createElement('img');
        t.src = img.currentSrc || img.src;
        t.alt = '';
        t.loading = 'lazy';
        t.width = 64; t.height = 64;
        btn.appendChild(t);
        btn.addEventListener('click', () => showSlide(i));
        thumbsWrap.appendChild(btn);
        thumbs.push(btn);
      });
    }

    function showSlide(index) {
      slides.forEach((s, i) => {
        const active = i === index;
        s.hidden = !active;
        s.classList.toggle('is-active', active);
        if (active) { const img = $('img', s); if (img && img.loading === 'lazy') img.loading = 'eager'; }
      });
      thumbs.forEach((t, i) => t.setAttribute('aria-current', String(i === index)));
    }
    const activeIndex = slides.findIndex((s) => s.classList.contains('is-active'));
    if (thumbs.length) showSlide(activeIndex < 0 ? 0 : activeIndex);

    function onVariantChange(id) {
      const v = variants.find((x) => String(x.id) === String(id));
      if (!v) return;
      if (priceEl) priceEl.textContent = v.price;
      const stickyPrice = $('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = v.price;
      if (selectedLabel) selectedLabel.textContent = v.title;
      if (addBtn) {
        addBtn.disabled = !v.available;
        if (addLabel) addLabel.textContent = v.available ? strings.addToCart : strings.soldOut;
      }
      const slideIndex = slides.findIndex((s) => (s.dataset.variantIds || '').split(',').includes(String(v.id)));
      if (slideIndex > -1) showSlide(slideIndex);
      if (isProductPage && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    box.addEventListener('change', (e) => {
      if (e.target.matches('[data-variant-radio], [data-variant-select]')) onVariantChange(e.target.value);
    });

    // Quantity stepper
    $$('[data-qty]', box).forEach((wrap) => {
      const input = $('input', wrap);
      $$('[data-qty-step]', wrap).forEach((b) => b.addEventListener('click', () => {
        const next = Math.max(1, Math.min(Number(input.max) || 99, (parseInt(input.value, 10) || 1) + Number(b.dataset.qtyStep)));
        input.value = next;
      }));
    });

    // Hover zoom (fine pointers only)
    if (stage && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      stage.addEventListener('pointerenter', () => stage.classList.add('is-zooming'));
      stage.addEventListener('pointerleave', () => stage.classList.remove('is-zooming'));
      stage.addEventListener('pointermove', (e) => {
        const r = stage.getBoundingClientRect();
        stage.style.setProperty('--zx', `${((e.clientX - r.left) / r.width) * 100}%`);
        stage.style.setProperty('--zy', `${((e.clientY - r.top) / r.height) * 100}%`);
      });
      $$('.buy__img', stage).forEach((img) => { img.style.transformOrigin = 'var(--zx, 50%) var(--zy, 50%)'; });
    }

    // AJAX add to cart → drawer (falls back to normal submit)
    if (form && cfg.cartType === 'drawer' && drawer.el && window.fetch && window.FormData) {
      const errorEl = $('[data-form-error]', box);
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (addBtn.disabled) return;
        if (errorEl) errorEl.hidden = true;
        addBtn.classList.add('is-loading');
        addBtn.setAttribute('aria-busy', 'true');
        if (addLabel) addLabel.textContent = strings.adding;
        const body = new FormData(form);
        body.append('sections', 'cart-drawer');
        body.append('sections_url', window.location.pathname);
        try {
          const res = await fetch(`${routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body });
          const data = await res.json();
          if (!res.ok || data.status) throw new Error(data.description || data.message || strings.cartError);
          drawer.render(data.sections && data.sections['cart-drawer']);
          drawer.open(addBtn);
        } catch (err) {
          if (errorEl) { errorEl.textContent = err.message || strings.cartError; errorEl.hidden = false; }
        } finally {
          addBtn.classList.remove('is-loading');
          addBtn.removeAttribute('aria-busy');
          const checked = $('[data-variant-radio]:checked, [data-variant-select]', box);
          const v = checked ? variants.find((x) => String(x.id) === String(checked.value)) : null;
          if (addLabel) addLabel.textContent = v && !v.available ? strings.soldOut : strings.addToCart;
        }
      });
    }
  });

  /* ---------- Cart drawer: events ---------- */
  if (drawer.el && cfg.cartType === 'drawer') {
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-cart-open]');
      if (opener) { e.preventDefault(); drawer.open(opener); return; }
      if (e.target.closest('[data-cart-close]') && drawer.el.contains(e.target)) {
        const link = e.target.closest('a[href]');
        drawer.close();
        if (!link) e.preventDefault();
        return;
      }
      const step = e.target.closest('[data-line-step]');
      const remove = e.target.closest('[data-line-remove]');
      if ((step || remove) && drawer.el.contains(e.target)) {
        e.preventDefault();
        const line = e.target.closest('[data-line-key]');
        const input = $('[data-line-qty]', line);
        const qty = remove ? 0 : Math.max(0, (parseInt(input.value, 10) || 0) + Number(step.dataset.lineStep));
        drawer.change(line.dataset.lineKey, qty);
      }
    });
    drawer.el.addEventListener('change', (e) => {
      if (e.target.matches('[data-line-qty]')) {
        const line = e.target.closest('[data-line-key]');
        drawer.change(line.dataset.lineKey, Math.max(0, parseInt(e.target.value, 10) || 0));
      }
    });
    document.addEventListener('keydown', (e) => {
      if (drawer.el.hidden) return;
      if (e.key === 'Escape') drawer.close();
      if (e.key === 'Tab') {
        const focusables = $$('a[href], button:not([disabled]), input:not([disabled]), textarea, [tabindex]:not([tabindex="-1"])', drawer.el).filter((n) => n.offsetParent !== null);
        if (!focusables.length) return;
        const first = focusables[0], last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }

  /* ---------- Cart page: quantity steppers update and reload ---------- */
  const cartPage = $('[data-cart-page]');
  if (cartPage && window.fetch) {
    const update = async (line, qty) => {
      cartPage.classList.add('is-busy');
      try {
        await fetch(`${routes.cartChange}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: line.dataset.lineKey, quantity: qty }) });
      } finally { window.location.reload(); }
    };
    cartPage.addEventListener('click', (e) => {
      const step = e.target.closest('[data-line-step]');
      const remove = e.target.closest('[data-line-remove]');
      if (!step && !remove) return;
      e.preventDefault();
      const line = e.target.closest('[data-line-key]');
      const input = $('[data-line-qty]', line);
      update(line, remove ? 0 : Math.max(0, (parseInt(input.value, 10) || 0) + Number(step.dataset.lineStep)));
    });
    cartPage.addEventListener('change', (e) => {
      if (e.target.matches('[data-line-qty]')) update(e.target.closest('[data-line-key]'), Math.max(0, parseInt(e.target.value, 10) || 0));
    });
  }

  sticky.init();
})();